// useWeekStart — the seven days a week may start on, as a picker's source (ADR-080
// §3 — the CODE kind), each named by `Intl` in the viewer's language ("Monday" ·
// « lundi » · «الاثنين»). ISO numbering (1 = Monday … 7 = Sunday — the project's
// `week_start`, ADR-082 §4), the names read off a known week (2024-01-01 is a
// Monday) so no list is hand-kept. Built once per language.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { WeekStart } from '@ems-app/schemas/common/period';
import { codeSource, type CodeSource } from './useOptions.js';
import { useProject } from './useProject.js';

export const WEEK_STARTS: readonly WeekStart[] = [1, 2, 3, 4, 5, 6, 7];

/** The weekday's name in a language — ISO numbered. */
export function weekdayName(day: WeekStart, language: string): string {
  const monday = Date.UTC(2024, 0, 1);
  return new Intl.DateTimeFormat(language, { weekday: 'long', timeZone: 'UTC' }).format(new Date(monday + (day - 1) * 86_400_000));
}

/** The PROJECT's week start (ADR-082 §4) — the day "This week" starts on and the first column of every calendar
 *  grid, never the language's. The project read is the frame's (cached); Monday stands in for the frame it could
 *  take to land. */
export function useWeekStart(projectId: string): WeekStart {
  return (useProject(projectId).data?.week_start as WeekStart | undefined) ?? 1;
}

export function useWeekStartSource(): CodeSource {
  const { i18n } = useTranslation();
  return useMemo(
    () => codeSource(`week-start:${i18n.language}`, WEEK_STARTS.map((d) => ({ id: String(d), label: weekdayName(d, i18n.language), pickable: true, group: null }))),
    [i18n.language],
  );
}
